import type { MinimalUser } from '@pbk-web/types';
import { ImageSize, ProfileAvatar } from '@pbk-web/ui';
import { cx, StringUtils } from '@pbk-web/utils';
import type { KeyboardEvent } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fieldClasses } from '../../ui/styles';
import { mentionName } from './MentionChip';

/** How long the editor keeps the keyboard after `@`, so carrying on typing closes the menu instead. */
const TYPING_LEEWAY_MS = 300;

/** The list never scrolls: past this many matches, the search narrows it down. */
export const MAX_VISIBLE_MENTIONS = 6;

type Props = {
  users: readonly MinimalUser[];
  onPick: (user: MinimalUser) => void;
  /** Escape: closes the menu, leaving the `@` as typed. */
  onCancel: () => void;
};

/** The `@` menu: a search over the users' full names and emails, with the match highlighted. */
export const MentionsMenu = ({ users, onPick, onCancel }: Props) => {
  const { t } = useTranslation('common');
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => inputRef.current?.focus(), TYPING_LEEWAY_MS);
    return () => clearTimeout(timeout);
  }, []);

  // Matched the way `highlightText` highlights (the whole query, anywhere), so every row shows why it's there
  const needle = query.trim();
  const lowerNeedle = needle.toLowerCase();
  const matches = needle
    ? users.filter((user) => [mentionName(user), user.email ?? ''].some((text) => text.toLowerCase().includes(lowerNeedle)))
    : users;
  const visible = matches.slice(0, MAX_VISIBLE_MENTIONS);
  const activeIndex = Math.min(active, visible.length - 1);
  const optionId = (index: number) => `${id}-option-${index}`;

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (visible[activeIndex]) onPick(visible[activeIndex]);
    } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && visible.length > 0) {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((activeIndex + step + visible.length) % visible.length);
    }
  };

  return (
    <div className="flex w-64 flex-col gap-1 p-1">
      <input
        ref={inputRef}
        role="combobox"
        aria-expanded
        aria-controls={`${id}-list`}
        aria-activedescendant={visible.length > 0 ? optionId(activeIndex) : undefined}
        aria-label={t('editor.mentions.search')}
        placeholder={t('editor.mentions.search')}
        className={fieldClasses}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
      />
      {visible.length === 0 ? (
        <p className="text-pbk-12 text-text-2 px-2 py-1.5">{t('editor.mentions.empty')}</p>
      ) : (
        <ul id={`${id}-list`} role="listbox" aria-label={t('editor.mentions.search')}>
          {visible.map((user, index) => (
            <li
              key={user.id}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              className={cx(
                'text-pbk-14 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5',
                index === activeIndex && 'bg-background-mid-1',
              )}
              onPointerMove={() => setActive(index)}
              onClick={() => onPick(user)}
            >
              <ProfileAvatar user={user} size={ImageSize.XS} />
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{StringUtils.highlightText(mentionName(user), needle)}</span>
                {user.email && <span className="text-pbk-12 text-text-2 truncate">{StringUtils.highlightText(user.email, needle)}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {matches.length > MAX_VISIBLE_MENTIONS && <p className="text-pbk-12 text-text-2 px-2 pb-1">{t('editor.mentions.more')}</p>}
    </div>
  );
};
